import { useState } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, KeyRound, LogIn } from 'lucide-react';
import AuthLayout from '@/components/AuthLayout';
import { authApi } from '@/services/auth';
import { ApiError } from '@/lib/api-client';
import { passwordSchema, PASSWORD_HINT } from '@/lib/password-policy';
import { logAuthStage } from '@/lib/auth-log';

const formSchema = z.object({
    // Shared account password policy — kept identical to the back-end and to
    // the other password forms (see @/lib/password-policy).
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

export default function ResetPasswordPage() {
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token');
    // const email = searchParams.get('email'); // For future use with API

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            newPassword: '',
            confirmPassword: '',
        },
    });

    const onSubmit = async (data: FormValues) => {
        try {
            setIsSubmitting(true);

            if (!token) {
                throw new Error('No reset token provided');
            }

            // Call the real reset password API
            const response = await authApi.resetPassword({
                token,
                newPassword: data.newPassword
            });

            toast({
                title: 'Password Reset Successful!',
                description: response.message,
                variant: 'success',
            });

            // Show success state
            setIsSuccess(true);

        } catch (error) {
            const status = error instanceof ApiError ? error.status : undefined;
            logAuthStage('reset-password-error', {
                status,
                message: error instanceof Error ? error.message : String(error),
            });

            let errorMessage = 'Failed to reset password. Please try again.';

            if (error instanceof ApiError) {
                // 401 here means the reset link/token is the problem — make that
                // explicit so the user knows to request a fresh link.
                errorMessage =
                    status === 401
                        ? `${error.message}. Your reset link may have expired or already been used — request a new one.`
                        : error.message;
            }

            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoToLogin = () => {
        navigate('/login');
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    // If no token, redirect to forgot password. Use a <Navigate> element
    // rather than calling navigate() during render (a render-phase side effect).
    if (!token) {
        return <Navigate to="/forgot-password" replace />;
    }

    // Success State
    if (isSuccess) {
        return (
            <AuthLayout imageSrc="/forgot.jpg" imageAlt="Password Reset Success">
                <div className="text-center">
                    {/* Success Icon/Image */}
                    <div className="mb-8">
                        <img
                            src="/success.png"
                            alt="Success"
                            className="mx-auto mb-6 drop-"
                        />
                    </div>

                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="font-newsreader text-2xl font-semibold tracking-tight tracking-tight text-foreground mb-3">
                            Password Reset Successful
                        </h1>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                            Your password's been updated. You can now log in securely using your new password.
                        </p>
                    </div>

                    {/* Go to Login Button */}
                    <div className="w-full">
                        <Button
                            onClick={handleGoToLogin}
                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 rounded-xl text-sm font-bold tracking-tight flex items-center justify-center gap-2 transition-all duration-200 "
                        >
                            Go to Login
                            <LogIn className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </AuthLayout>
        );
    }

    // Reset Form State
    return (
        <AuthLayout imageSrc="/forgot.jpg" imageAlt="Reset Password">
            {/* Header */}
            <div className="mb-8 text-left">
                <h1 className="font-newsreader text-2xl font-semibold tracking-tight tracking-tight text-foreground mb-3">
                    Reset Your Password
                </h1>
                <p className="text-muted-foreground text-sm leading-relaxed">
                    Your new password must be different to previously used passwords.
                </p>
            </div>

            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                >
                    {/* New Password */}
                    <FormField
                        control={form.control}
                        name="newPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-foreground font-semibold text-sm">
                                    New Password
                                </FormLabel>
                                <FormControl>
                                    <div className="relative mt-2">
                                        <Input
                                            type={showNewPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            {...field}
                                            className="pr-12 h-12 border-border focus:border-secondary focus:ring-secondary rounded-xl text-base"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-1 top-1 h-10 w-10 rounded-lg hover:bg-muted"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                        >
                                            {showNewPassword ? (
                                                <EyeOff className="h-5 w-5 text-muted-foreground/70" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-muted-foreground/70" />
                                            )}
                                        </Button>
                                    </div>
                                </FormControl>
                                <FormDescription className="text-xs">
                                    {PASSWORD_HINT}
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Confirm New Password */}
                    <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-foreground font-semibold text-sm">
                                    Confirm New Password
                                </FormLabel>
                                <FormControl>
                                    <div className="relative mt-2">
                                        <Input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            {...field}
                                            className="pr-12 h-12 border-border focus:border-secondary focus:ring-secondary rounded-xl text-base"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-1 top-1 h-10 w-10 rounded-lg hover:bg-muted"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-5 w-5 text-muted-foreground/70" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-muted-foreground/70" />
                                            )}
                                        </Button>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Reset Password Button */}
                    <div className="mt-8">
                        <Button
                            type="submit"
                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 h-12 rounded-xl text-sm font-bold tracking-tight flex items-center justify-center gap-2 transition-all duration-200 "
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Resetting Password...
                                </>
                            ) : (
                                <>
                                    Reset Password
                                    <KeyRound className="w-5 h-5" />
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </Form>

            {/* Back to Login */}
            <div className="mt-4 text-center">
                <span className="text-muted-foreground text-sm">Remembered your credentials? </span>
                <Button
                    type="button"
                    variant="link"
                    onClick={handleBackToLogin}
                    className="text-foreground-soft hover:text-foreground hover:underline p-0 h-auto text-sm font-semibold transition-colors duration-200"
                >
                    Sign in instead
                </Button>
            </div>
        </AuthLayout>
    );
}
